import React, { useState } from 'react';
import { HealthBuddy } from '../components/HealthBuddy';
import { ChevronRightIcon, ChevronDownIcon, SlidersIcon } from '../components/icons';
import { FEATURES, RECOMMENDATIONS, FAVOURITES, SUPPORT, TINTS, ModalKind } from '../data/mock';
import { useDashboardConfig, orderItems } from '../data/dashboardConfig';
/**
 * The main dashboard — home screen, launch point, conversational hub.
 * Feature cards + contextual recommendations + the buddy's home.
 * The persistent chat dock is rendered by App so it survives navigation.
 */
type SectionKey = 'heads-up' | 'favourites' | 'spaces' | 'support';
export function Dashboard({
  onFeature,
  onOpenModal,
  onSettings,
  onToggleChat
}: {
  onFeature: (id: string) => void;
  onOpenModal: (modal: ModalKind, start?: string) => void;
  onSettings: () => void;
  onToggleChat: () => void;
}) {
  const {
    getOrder,
    getLimit,
    resetSection
  } = useDashboardConfig();
  const [expanded, setExpanded] = useState<Record<SectionKey, boolean>>({
    'heads-up': false,
    favourites: false,
    spaces: false,
    support: false
  });
  // a section shows its configured limit, or every item when toggled open
  const visible = <T extends {
    id: string;
  },>(key: SectionKey, items: T[]): T[] => {
    const ordered = orderItems(items, getOrder(key));
    return expanded[key] ? ordered : ordered.slice(0, getLimit(key));
  };
  // "Show all N" expands in place; once open the toggle resets preferences
  const SectionToggle = ({
    sectionKey,
    total,
    label
  }: {
    sectionKey: SectionKey;
    total: number;
    label: string;
  }) => {
    const isOpen = expanded[sectionKey];
    return <button onClick={() => {
      if (isOpen) {
        resetSection(sectionKey);
        setExpanded(e => ({
          ...e,
          [sectionKey]: false
        }));
      } else {
        setExpanded(e => ({
          ...e,
          [sectionKey]: true
        }));
      }
    }} className="mt-3 flex w-full items-center justify-center gap-1 px-4 py-1 text-[13px] font-bold text-teal-deep transition-colors hover:text-teal-deep/70" data-id="element-904">
        {isOpen ? 'Reset preferences' : `Show all ${total} ${label}`}
        <ChevronDownIcon size={14} strokeWidth={2.4} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-905" />
      </button>;
  };
  const handleSupport = (item: (typeof SUPPORT)[number]) => {
    if (item.modal) {
      onOpenModal(item.modal, item.start);
      return;
    }
    if (item.tel) {
      window.location.href = `tel:${item.tel.replace(/\s/g, '')}`;
    }
  };
  return <div className="h-full overflow-y-auto pb-48" data-id="element-906">
      {/* decorative warmth behind the header */}
      <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-butter-soft opacity-80 blur-2xl" data-id="element-907" />
      <div className="pointer-events-none absolute -left-20 top-40 h-44 w-44 rounded-full bg-teal-soft opacity-70 blur-2xl" data-id="element-908" />

      {/* header */}
      <header className="relative px-5 pt-4" data-id="element-909">
        <div className="flex items-start justify-between" data-id="element-910">
          <div className="dhp-fade-up" data-id="element-911">
            <p className="text-[11.5px] font-bold uppercase tracking-[0.18em] text-ink-faint" data-id="element-912">
              Tuesday 2 June
            </p>
            <h1 className="font-display mt-1.5 whitespace-nowrap text-[22px] font-medium leading-[1.1] text-ink" data-id="element-913">
              Good morning,
              <br data-id="element-914" />
              <span className="text-[27px] font-bold text-teal-deep" data-id="element-915">Maya</span>
            </h1>
          </div>
          <div className="flex items-center gap-2.5" data-id="element-916">
            <button onClick={onSettings} className="flex h-10 w-10 items-center justify-center rounded-full bg-cream text-ink-soft shadow-card transition-transform hover:scale-105 active:scale-90" data-id="element-917">
              <SlidersIcon size={18} data-id="element-918" />
            </button>
            <button onClick={onToggleChat} aria-label="Toggle chat with Sunny" className="rounded-full transition-transform hover:scale-105 active:scale-90" data-id="element-919">
              <HealthBuddy size={56} mood="idle" className="dhp-float" data-id="element-920" />
            </button>
          </div>
        </div>
      </header>

      {/* contextual recommendations — the companion notices things, grouped in one card */}
      <section className="relative mt-6 px-5" data-id="element-921">
        <p className="mb-2.5 text-[11.5px] font-bold uppercase tracking-[0.18em] text-ink-faint" data-id="element-922">
          Heads Up
        </p>
        <div className="dhp-stagger space-y-2.5" data-id="element-923">
          {visible('heads-up', RECOMMENDATIONS).map(rec => {
          const tint = TINTS[rec.tint];
          const Icon = rec.icon;
          return <button key={rec.id} onClick={() => onOpenModal(rec.modal)} className="flex w-full items-center gap-3.5 rounded-card border border-line bg-cream px-4 py-3.5 text-left shadow-card transition-all hover:-translate-y-0.5 hover:shadow-card-hover active:scale-[0.98]" data-id="element-924">
                <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${tint.soft} ${tint.text}`} data-id="element-925">
                  <Icon size={22} data-id="element-926" />
                </span>
                <div className="min-w-0 flex-1" data-id="element-927">
                  <h3 className="font-display text-[16px] font-semibold leading-snug text-ink" data-id="element-928">
                    {rec.title}
                  </h3>
                  <p className="mt-0.5 truncate text-[13px] leading-snug text-ink-soft" data-id="element-929">
                    {rec.body}
                  </p>
                </div>
                <ChevronRightIcon size={18} strokeWidth={2.4} className="shrink-0 text-ink-faint" data-id="element-930" />
              </button>;
        })}
        </div>
        <SectionToggle sectionKey="heads-up" total={RECOMMENDATIONS.length} label="notifications" data-id="element-931" />
      </section>

      {/* favourites — quick access to the user's pinned tools */}
      <section className="relative mt-7 px-5" data-id="element-932">
        <p className="mb-2.5 text-[11.5px] font-bold uppercase tracking-[0.18em] text-ink-faint" data-id="element-933">
          Favourites
        </p>
        <div className="dhp-stagger space-y-2.5" data-id="element-934">
          {visible('favourites', FAVOURITES).map(fav => {
          const tint = TINTS[fav.tint];
          const Icon = fav.icon;
          return <button key={fav.id} onClick={() => onOpenModal(fav.modal, fav.start)} className="flex w-full items-center gap-3.5 rounded-card border border-line bg-cream px-4 py-3.5 text-left shadow-card transition-all hover:-translate-y-0.5 hover:shadow-card-hover active:scale-[0.98]" data-id="element-935">
                <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${tint.soft} ${tint.text}`} data-id="element-936">
                  <Icon size={22} data-id="element-937" />
                </span>
                <div className="min-w-0 flex-1" data-id="element-938">
                  <h3 className="font-display text-[16px] font-semibold leading-snug text-ink" data-id="element-939">
                    {fav.title}
                  </h3>
                </div>
                <ChevronRightIcon size={18} strokeWidth={2.4} className="shrink-0 text-ink-faint" data-id="element-940" />
              </button>;
        })}
        </div>
        <SectionToggle sectionKey="favourites" total={FAVOURITES.length} label="favourites" data-id="element-941" />
      </section>

      {/* feature cards — the eight care spaces */}
      <section className="relative mt-7 px-5" data-id="element-942">
        <p className="mb-2.5 text-[11.5px] font-bold uppercase tracking-[0.18em] text-ink-faint" data-id="element-943">
          Spaces
        </p>
        <div className="dhp-stagger space-y-2.5" data-id="element-944">
          {visible('spaces', FEATURES).map(feature => {
          const tint = TINTS[feature.tint];
          const Icon = feature.icon;
          return <button key={feature.id} onClick={() => onFeature(feature.id)} className="flex w-full items-center gap-3.5 rounded-card border border-line bg-cream px-4 py-3.5 text-left shadow-card transition-all hover:-translate-y-0.5 hover:shadow-card-hover active:scale-[0.98]" data-id="element-945">
                <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${tint.soft} ${tint.text}`} data-id="element-946">
                  <Icon size={22} data-id="element-947" />
                </span>
                <div className="min-w-0 flex-1" data-id="element-948">
                  <h3 className="font-display text-[16px] font-semibold leading-snug text-ink" data-id="element-949">
                    {feature.title}
                  </h3>
                </div>
                <ChevronRightIcon size={18} strokeWidth={2.4} className="shrink-0 text-ink-faint" data-id="element-950" />
              </button>;
        })}
        </div>
        <SectionToggle sectionKey="spaces" total={FEATURES.length} label="spaces" data-id="element-951" />
      </section>

      {/* support — help & emergency access (always last) */}
      <section className="relative mt-7 px-5" data-id="element-952">
        <p className="mb-2.5 text-[11.5px] font-bold uppercase tracking-[0.18em] text-ink-faint" data-id="element-953">
          Support
        </p>
        <div className="dhp-stagger space-y-2.5" data-id="element-954">
          {visible('support', SUPPORT).map(item => {
          const tint = TINTS[item.tint];
          const Icon = item.icon;
          return <button key={item.id} onClick={() => handleSupport(item)} className="flex w-full items-center gap-3.5 rounded-card border border-line bg-cream px-4 py-3.5 text-left shadow-card transition-all hover:-translate-y-0.5 hover:shadow-card-hover active:scale-[0.98]" data-id="element-955">
                <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${tint.soft} ${tint.text}`} data-id="element-956">
                  <Icon size={22} data-id="element-957" />
                </span>
                <div className="min-w-0 flex-1" data-id="element-958">
                  <h3 className="font-display text-[16px] font-semibold leading-snug text-ink" data-id="element-959">
                    {item.title}
                  </h3>
                </div>
                <ChevronRightIcon size={18} strokeWidth={2.4} className="shrink-0 text-ink-faint" data-id="element-960" />
              </button>;
        })}
        </div>
        <SectionToggle sectionKey="support" total={SUPPORT.length} label="support options" data-id="element-961" />
      </section>
    </div>;
}